import React, { useCallback, useEffect, useState } from 'react';
import Loader from './Loader';
import useFocusTrap from '../hooks/useFocusTrap';

/**
 * Super Admin modal to create/update Brand Admin or Branch Admin login credentials.
 */
const PortalAdminCredentialsModal = ({
  open,
  title,
  subtitle,
  loading,
  saving,
  initialAdmin,
  onClose,
  onSave
}) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleEscape = useCallback(() => {
    if (!saving) onClose();
  }, [onClose, saving]);

  const dialogRef = useFocusTrap(open, { onEscape: handleEscape });

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initialAdmin?.name || '',
      email: initialAdmin?.email || '',
      password: '',
      confirmPassword: ''
    });
    setError('');
    setFieldErrors({});
  }, [open, initialAdmin]);

  if (!open) return null;

  const isUpdate = Boolean(initialAdmin?.id);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const nextErrors = {};

    const email = String(form.email || '').trim();
    if (!email) {
      nextErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = 'Enter a valid email address';
    }

    const password = String(form.password || '');
    if (!isUpdate && password.length < 6) {
      nextErrors.password = 'Password is required (min 6 characters)';
    } else if (password && password.length < 6) {
      nextErrors.password = 'Password must be at least 6 characters';
    }
    if (password && password !== form.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      setError('Please fix the highlighted fields and try again.');
      return;
    }
    setFieldErrors({});

    const payload = {
      name: String(form.name || '').trim(),
      email
    };
    if (password) payload.password = password;

    try {
      await onSave(payload);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save credentials');
    }
  };

  return (
    <div
      className="admin-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-admin-cred-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480 }}
      >
        <div className="admin-card admin-modal-card">
          <h2
            id="portal-admin-cred-title"
            style={{
              fontFamily: "'Lato', sans-serif",
              fontWeight: 700,
              fontSize: '16px',
              color: '#FFFFFF',
              marginBottom: subtitle ? '8px' : '20px'
            }}
          >
            {title}
          </h2>
          {subtitle && (
            <p style={{ marginTop: 0, marginBottom: '16px', color: '#A0A0A0', fontSize: '0.9rem' }}>
              {subtitle}
            </p>
          )}

          {loading ? (
            <Loader variant="dark" fullScreen={false} />
          ) : (
            <form onSubmit={handleSubmit}>
              {initialAdmin?.email && (
                <p style={{ marginTop: 0, color: '#A0A0A0', fontSize: '0.85rem' }}>
                  Current login: <strong style={{ color: '#fff' }}>{initialAdmin.email}</strong>
                  {initialAdmin.lastLogin
                    ? ` · last login ${new Date(initialAdmin.lastLogin).toLocaleString()}`
                    : ' · never logged in'}
                </p>
              )}

              {error && (
                <div className="error" role="alert">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="portal-admin-name">Name</label>
                <input
                  id="portal-admin-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Admin display name"
                  disabled={saving}
                  data-autofocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="portal-admin-email">Login email *</label>
                <input
                  id="portal-admin-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className={fieldErrors.email ? 'input-error' : undefined}
                  required
                  disabled={saving}
                  autoComplete="off"
                />
                {fieldErrors.email && <span className="form-error-text">{fieldErrors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="portal-admin-password">
                  {isUpdate ? 'New password (leave blank to keep current)' : 'Password *'}
                </label>
                <input
                  id="portal-admin-password"
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  className={fieldErrors.password ? 'input-error' : undefined}
                  required={!isUpdate}
                  minLength={isUpdate ? undefined : 6}
                  disabled={saving}
                  autoComplete="new-password"
                />
                {fieldErrors.password && <span className="form-error-text">{fieldErrors.password}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="portal-admin-confirm">Confirm password</label>
                <input
                  id="portal-admin-confirm"
                  type="password"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  className={fieldErrors.confirmPassword ? 'input-error' : undefined}
                  required={!isUpdate || Boolean(form.password)}
                  disabled={saving}
                  autoComplete="new-password"
                />
                {fieldErrors.confirmPassword && (
                  <span className="form-error-text">{fieldErrors.confirmPassword}</span>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : isUpdate ? 'Update credentials' : 'Create admin login'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalAdminCredentialsModal;
